import { array, concat } from '@ember/helper';
import { get } from '@ember/object';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import add from 'ember-math-helpers/helpers/add';
import ScaleSurveyEditionFormComponent from 'parog-web/components/abstract/scale-survey-edition-form';
import type { Payload } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import PhqNineDeclaredJournalEvent from 'parog-web/models/phq-nine-declared-journal-event';
import phqNineScoreDescription from 'parog-web/utils/phq-nine-score-description';
import TwoButtonTabs from 'ui-kit/components/two-button-tabs';

export default class PhqNineEditionFormComponent extends ScaleSurveyEditionFormComponent {
  static override forcDisplayName =
    "Questionnaire d'évaluation de la dépression - PHQ-9";
  static override fullComponentId = 'scale-surveys/phq-nine-edition-form';
  static override journalEventClass = PhqNineDeclaredJournalEvent;

  override get maximalScore(): ScaleSurveyEditionFormComponent['maximalScore'] {
    return 27;
  }

  override get numberOfItems(): ScaleSurveyEditionFormComponent['numberOfItems'] {
    return 9;
  }

  override _computeScore(
    payload: Payload
  ): ScaleSurveyEditionFormComponent['score'] {
    const number = this.questionIds.reduce(
      (total, index) => total + parseInt(get(payload, index) as string),
      0
    );

    return {
      number,
      description: phqNineScoreDescription(number)
    };
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <TwoButtonTabs
          class='large'
          @selected={{if
            (changesetGet this.changeset 'isTotalScoreFilled')
            'second'
            'first'
          }}
          @onClick={{this.onTabButtonClicked}}
        >
          <:firstButton>Répondre aux questions</:firstButton>
          <:secondButton>Renseigner le score</:secondButton>

          <:firstTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>

            <h4>Au cours des 2 dernières semaines, selon quelle fréquence
              avez-vous été gêné(e) par les problèmes suivants ?</h4>

            {{#each
              (array
                'Peu d’intérêt ou de plaisir à faire les choses'
                'Être triste, déprimé(e) ou désespéré(e)'
                'Difficultés à s’endormir ou à rester endormi(e), ou dormir trop'
                'Se sentir fatigué(e) ou manquer d’énergie'
                'Avoir peu d’appétit ou manger trop'
                'Avoir une mauvaise opinion de soi-même, ou avoir le sentiment d’être nul(le), ou d’avoir déçu sa famille ou s’être déçu(e) soi-même'
                'Avoir du mal à se concentrer, par exemple, pour lire le journal ou regarder la télévision'
                'Bouger ou parler si lentement que les autres auraient pu le remarquer. Ou au contraire, être si agité(e) que vous avez eu du mal à tenir en place par rapport à d’habitude'
                'Penser qu’il vaudrait mieux mourir ou envisager de vous faire du mal d’une manière ou d’une autre'
              )
              as |title index|
            }}
              <div class='field'>
                <Form.FieldsRadioGroup
                  @title={{concat (add index 1) '. ' title}}
                  @propertyName='{{index}}'
                  as |Group|
                >
                  <Group.FieldRadio @value='0' @label='Jamais' />
                  <Group.FieldRadio @value='1' @label='Plusieurs jours' />
                  <Group.FieldRadio
                    @value='2'
                    @label='Plus de la moitié du temps'
                  />
                  <Group.FieldRadio
                    @value='3'
                    @label='Presque tous les jours'
                  />
                </Form.FieldsRadioGroup>
              </div>
            {{/each}}

            <div class='ui message' data-test-score>
              <strong>Score : </strong>
              {{#if this.isChangesetValid}}
                {{this.score.number}}
                –
                {{this.score.description}}
              {{else}}
                veuillez remplir tous les champs afin d'obtenir le score.
              {{/if}}
            </div>
          </:firstTab>

          <:secondTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>
            <div class='field'>
              <Form.NumberInput
                @labelName='Score total'
                @propertyName='total'
                @type='integer'
              />
            </div>
          </:secondTab>
        </TwoButtonTabs>
      </:content>
    </Provider>
  </template>
}
